import Phaser from 'phaser';
import { Enemy } from '../sprites/Enemy';
import { EvilEgg } from '../sprites/EvilEgg';
import { AngryBacon } from '../sprites/AngryBacon';
import { MenacingCoffee } from '../sprites/MenacingCoffee';
import { ClassicAlien } from '../sprites/ClassicAlien';
import type { AlienType } from '../sprites/ClassicAlien';
import { EnemyProjectile } from '../sprites/EnemyProjectile';
import { GAME_WIDTH, BOUNDS } from '../config/constants';
import { getDifficultySettings } from '../config/difficultySettings';

export type EnemyType = 'evilEgg' | 'angryBacon' | 'menacingCoffee' | 'classicAlien';

export interface WaveConfig {
  rows: number;
  cols: number;
  enemySpacingX: number;
  enemySpacingY: number;
  startY: number;
  moveSpeed: number;
  dropDistance: number;
  speedIncreasePerKill: number;
  enabledEnemyTypes: EnemyType[];
}

export class WaveManager {
  private scene: Phaser.Scene;
  private enemies: Phaser.Physics.Arcade.Group;
  private enemyProjectiles: Phaser.Physics.Arcade.Group;
  private config: WaveConfig;
  
  private moveDirection: number = 1;
  private currentSpeed: number;
  private waveNumber: number = 1;
  private totalEnemies: number = 0;
  private enemiesKilled: number = 0;

  constructor(scene: Phaser.Scene, enemyGroup: Phaser.Physics.Arcade.Group, projectileGroup: Phaser.Physics.Arcade.Group) {
    this.scene = scene;
    this.enemies = enemyGroup;
    this.enemyProjectiles = projectileGroup;
    
    const difficultySettings = getDifficultySettings();
    
    this.config = {
      rows: 5,
      cols: 11,
      enemySpacingX: 50,
      enemySpacingY: 40,
      startY: 80,
      moveSpeed: difficultySettings.enemyBaseSpeed,
      dropDistance: 20,
      speedIncreasePerKill: 0.5,
      enabledEnemyTypes: ['evilEgg', 'angryBacon', 'menacingCoffee', 'classicAlien'],
    };
    
    this.currentSpeed = this.config.moveSpeed;
  }

  spawnWave(): void {
    const totalWidth = (this.config.cols - 1) * this.config.enemySpacingX;
    const startX = (GAME_WIDTH - totalWidth) / 2;

    for (let row = 0; row < this.config.rows; row++) {
      for (let col = 0; col < this.config.cols; col++) {
        const x = startX + col * this.config.enemySpacingX;
        const y = this.config.startY + row * this.config.enemySpacingY;

        // Determine enemy type based on row and wave
        const enemy = this.createEnemyForPosition(row, col, x, y);
        if (enemy) {
          enemy.setGridPosition(col, row);
          this.enemies.add(enemy);
          this.totalEnemies++;
        }
      }
    }
  }

  private createEnemyForPosition(row: number, col: number, x: number, y: number): Enemy | null {
    const enabledTypes = this.config.enabledEnemyTypes;
    
    // Wave-based enemy distribution
    // Wave 1: Mostly eggs
    // Wave 2: Mix of eggs and bacon
    // Wave 3+: All types including coffee and aliens
    
    if (this.waveNumber === 1) {
      // Wave 1: All Evil Eggs
      return new EvilEgg(this.scene, x, y);
    }
    
    if (this.waveNumber === 2) {
      // Wave 2: Front rows = eggs, back rows = bacon
      if (row < 2 && enabledTypes.includes('angryBacon')) {
        return new AngryBacon(this.scene, x, y);
      }
      return new EvilEgg(this.scene, x, y);
    }
    
    // Wave 3+: Mixed formation based on row
    // Row 0 (back): Menacing Coffee (tanks)
    // Row 1: Classic Aliens
    // Row 2-3: Angry Bacon
    // Row 4 (front): Evil Eggs
    
    if (row === 0 && enabledTypes.includes('menacingCoffee')) {
      return new MenacingCoffee(this.scene, x, y);
    }
    
    if (row === 1 && enabledTypes.includes('classicAlien')) {
      const alienTypes: AlienType[] = ['squid', 'crab', 'octopus'];
      const alienType = alienTypes[col % 3];
      return new ClassicAlien(this.scene, x, y, alienType);
    }
    
    if ((row === 2 || row === 3) && enabledTypes.includes('angryBacon')) {
      return new AngryBacon(this.scene, x, y);
    }
    
    return new EvilEgg(this.scene, x, y);
  }

  update(time: number, delta: number): void {
    if (this.enemies.getLength() === 0) return;

    const enemies = this.enemies.getChildren() as Enemy[];
    
    const moveAmount = this.currentSpeed * (delta / 1000) * this.moveDirection;
    
    let shouldReverse = false;
    let leftMost = GAME_WIDTH;
    let rightMost = 0;

    enemies.forEach((enemy) => {
      if (enemy.active) {
        leftMost = Math.min(leftMost, enemy.x);
        rightMost = Math.max(rightMost, enemy.x);
      }
    });

    if (this.moveDirection > 0 && rightMost + moveAmount > BOUNDS.RIGHT - 20) {
      shouldReverse = true;
    } else if (this.moveDirection < 0 && leftMost + moveAmount < BOUNDS.LEFT + 20) {
      shouldReverse = true;
    }

    enemies.forEach((enemy) => {
      if (enemy.active) {
        if (shouldReverse) {
          enemy.y += this.config.dropDistance;
          // Update base positions for wobbling enemies
          if (enemy instanceof EvilEgg) {
            enemy.updateBaseY(enemy.y);
          }
        } else {
          enemy.x += moveAmount;
        }
        
        enemy.update(time, delta);
      }
    });

    if (shouldReverse) {
      this.moveDirection *= -1;
    }

    this.handleEnemyShooting(time);
  }

  private handleEnemyShooting(time: number): void {
    const enemies = this.enemies.getChildren() as Enemy[];
    const activeEnemies = enemies.filter(e => e.active);
    
    if (activeEnemies.length === 0) return;

    const difficultySettings = getDifficultySettings();
    const baseFireChance = 2000 / difficultySettings.enemyFireRate * 0.002;

    const columnBottomEnemies: Map<number, Enemy> = new Map();
    
    activeEnemies.forEach((enemy) => {
      const existing = columnBottomEnemies.get(enemy.gridX);
      if (!existing || enemy.gridY > existing.gridY) {
        columnBottomEnemies.set(enemy.gridX, enemy);
      }
    });

    columnBottomEnemies.forEach((enemy) => {
      // Different fire rates based on enemy type (modified by difficulty)
      let fireChance = baseFireChance;
      if (enemy instanceof AngryBacon) {
        fireChance = baseFireChance * 1.5; // Bacon fires more often
      } else if (enemy instanceof MenacingCoffee) {
        fireChance = baseFireChance * 0.75; // Coffee fires less but is dangerous
      }
      
      if (enemy.canFire(time) && Math.random() < fireChance) {
        this.fireProjectile(enemy, time);
      }
    });
  }

  private fireProjectile(enemy: Enemy, time: number): void {
    enemy.setLastFired(time);
    
    const difficultySettings = getDifficultySettings();
    // Scale projectile speed with difficulty
    const speedMultiplier = difficultySettings.enemyProjectileSpeed / 250;
    
    new EnemyProjectile(
      this.scene,
      enemy.x,
      enemy.y + 16,
      enemy.projectileTexture,
      enemy.projectileSpeed * speedMultiplier,
      this.enemyProjectiles
    );
  }

  onEnemyKilled(): void {
    this.enemiesKilled++;
    this.currentSpeed = this.config.moveSpeed + (this.enemiesKilled * this.config.speedIncreasePerKill);
  }

  isWaveComplete(): boolean {
    return this.enemies.getLength() === 0 || 
           this.enemies.getChildren().every(e => !e.active);
  }

  getEnemiesRemaining(): number {
    return this.enemies.getChildren().filter(e => e.active).length;
  }

  getWaveNumber(): number {
    return this.waveNumber;
  }

  nextWave(): void {
    this.waveNumber++;
    this.enemiesKilled = 0;
    this.totalEnemies = 0;
    
    const difficultySettings = getDifficultySettings();
    this.currentSpeed = this.config.moveSpeed * (1 + (this.waveNumber - 1) * difficultySettings.speedIncreasePerWave);
    this.moveDirection = 1;
    this.spawnWave();
  }

  setEnabledEnemyTypes(types: EnemyType[]): void {
    this.config.enabledEnemyTypes = types;
  }

  reset(): void {
    this.waveNumber = 1;
    this.enemiesKilled = 0;
    this.totalEnemies = 0;
    this.currentSpeed = this.config.moveSpeed;
    this.moveDirection = 1;
  }
}
